import React from 'react';
import { faCreditCard } from '@fortawesome/pro-solid-svg-icons/faCreditCard';
import { Box, CheckboxField, Group, Icon, Label, Link, Modal, Paragraph, Set, Stack } from 'bumbag';
import { Field } from 'formik';

import { GroupButton } from '@medipass/react-core';
import { faApplePay } from '@medipass/react-core';
import { faGoogle } from '@medipass/react-core';
import { PaymentMethod } from '@medipass/react-core/redux-form';
import { TRANSACTION_PAYMENT_OPTIONS } from '@medipass/utils/constants';
import { isHealthPoint, isHICAPS, isOHC } from '@medipass/utils/funders';
import sdk from '@medipass/web-sdk';

import { ApplePayAvailabilityStatus } from 'components/applePay';
import { canRenderGooglePayOption } from 'components/googlePay';
import RemovePaymentMethodModal from 'legacy/components/checkout/RemovePaymentMethodModal';

type Props = {
  amount?: string;
  applePayStatus?: ApplePayAvailabilityStatus;
  alwaysSavePatientPaymentMethod?: boolean;
  funderCode?: string;
  onChangeCardPaymentMethod: (...args: Array<any>) => any;
  onChangePaymentOption: (...args: Array<any>) => any;
  onUserChangePaymentOption?: (...args: Array<any>) => any;
  onRemovePaymentMethod: (...args: Array<any>) => any;
  cardPaymentMethods?: Array<Record<string, any>>;
  paymentOption?: string | undefined;
  selectedCardPaymentMethod?: Record<string, any>;
  showExternalPaymentProviders?: boolean;
  showPayInPerson?: boolean;
  showPaymentCard?: boolean;
  showRememberCardDetails?: boolean;
};
PaymentSelection.defaultProps = {
  amount: undefined,
  cardPaymentMethods: [],
  funderCode: undefined,
  paymentOption: undefined,
  selectedCardPaymentMethod: undefined,
  showExternalPaymentProviders: true,
  showPayInPerson: true,
  showPaymentCard: true,
  showRememberCardDetails: false,
  alwaysSavePatientPaymentMethod: false,
};

// @ts-expect-error TS(7031): Binding element 'fn' implicitly has an 'any' type.
function Effect({ fn }) {
  React.useEffect(fn, []); //eslint-disable-line

  return null;
}

export default function PaymentSelection(props: Props) {
  const {
    applePayStatus = 'unsupported',
    alwaysSavePatientPaymentMethod,
    funderCode,
    onChangeCardPaymentMethod,
    onChangePaymentOption,
    onUserChangePaymentOption,
    onRemovePaymentMethod,
    cardPaymentMethods,
    paymentOption,
    selectedCardPaymentMethod,
    showExternalPaymentProviders,
    showPayInPerson,
    showPaymentCard,
    showRememberCardDetails,
  } = props;
  const removePaymentMethodModal = Modal.useState();
  const [paymentMethodIdToRemove, setPaymentMethodIdToRemove] = React.useState(undefined);
  const shouldShowApplePayOption =
    showExternalPaymentProviders && (applePayStatus === 'checking' || applePayStatus === 'eligible');
  const shouldShowPayInPersonOption =
    // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
    showPayInPerson && (isHICAPS(funderCode) || isOHC(funderCode) || isHealthPoint(funderCode));
  const isApplePayChecking = applePayStatus === 'checking';
  const isApplePaySelected = paymentOption === TRANSACTION_PAYMENT_OPTIONS.APPLE_PAY;
  const shouldShowGooglePayOption = showExternalPaymentProviders && canRenderGooglePayOption();
  const paymentOptionCount = [
    shouldShowGooglePayOption,
    shouldShowApplePayOption,
    showPaymentCard,
    shouldShowPayInPersonOption,
  ].filter(Boolean).length;

  const handleSelectPaymentOption = (nextPaymentOption: string) => {
    onChangePaymentOption(nextPaymentOption);
    onUserChangePaymentOption?.(nextPaymentOption);
  };

  // @ts-expect-error TS(7006): Parameter 'paymentMethodId' implicitly has an 'any... Remove this comment to see the full error message
  function handleRemovePaymentMethod(paymentMethodId) {
    setPaymentMethodIdToRemove(paymentMethodId);
    removePaymentMethodModal.show();
  }

  async function onClickRemovePaymentMethod() {
    // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
    await sdk.selfCheckoutSessions.deletePaymentMethod(paymentMethodIdToRemove);
    await onRemovePaymentMethod(paymentMethodIdToRemove);
    removePaymentMethodModal.hide();
  }

  async function onClickCancelRemovePaymentMethod() {
    setPaymentMethodIdToRemove(undefined);
    removePaymentMethodModal.hide();
  }

  return (
    <Stack spacing="major-2">
      {paymentOptionCount > 1 && (
        <Stack spacing="major-1">
          <Label id="payment-selection-label">How do you want to pay?</Label>
          <Group aria-labelledby="payment-selection-label">
            {shouldShowGooglePayOption && (
              <React.Fragment>
                <Effect fn={() => onChangePaymentOption(TRANSACTION_PAYMENT_OPTIONS.GOOGLE_PAY)} />
                <GroupButton
                  aria-label="Google Pay"
                  // @ts-expect-error TS(2322): Type '{ children: string; onClick: () => void; pa... Remove this comment to see the full error message
                  onClick={() => handleSelectPaymentOption(TRANSACTION_PAYMENT_OPTIONS.GOOGLE_PAY)}
                  selected={paymentOption === TRANSACTION_PAYMENT_OPTIONS.GOOGLE_PAY}
                  flex="1"
                >
                  <Icon icon={faGoogle} type="font-awesome" marginRight="minor-1" />
                  Pay
                </GroupButton>
              </React.Fragment>
            )}
            {shouldShowApplePayOption && (
              <React.Fragment>
                <GroupButton
                  aria-label="Apple Pay"
                  data-testid={isApplePayChecking ? 'apple-pay-option-shell' : undefined}
                  // @ts-expect-error TS(2322): Type '{ children: string; onClick: () => void; pa... Remove this comment to see the full error message
                  disabled={isApplePayChecking}
                  onClick={() => {
                    if (isApplePayChecking) return;
                    handleSelectPaymentOption(TRANSACTION_PAYMENT_OPTIONS.APPLE_PAY);
                  }}
                  selected={isApplePaySelected}
                  flex="1"
                >
                  <Icon icon={faApplePay} fontSize="600" type="font-awesome" marginRight="minor-1" />
                </GroupButton>
              </React.Fragment>
            )}
            {showPaymentCard && (
              <GroupButton
                aria-label="Credit card"
                // @ts-expect-error TS(2322): Type '{ children: string; onClick: () => void; pa... Remove this comment to see the full error message
                onClick={() => handleSelectPaymentOption(TRANSACTION_PAYMENT_OPTIONS.PAYMENT_CARD)}
                selected={paymentOption === TRANSACTION_PAYMENT_OPTIONS.PAYMENT_CARD}
                flex="1"
              >
                {/* @ts-expect-error TS(2322): Type 'IconDefinition' is not assignable to type 's... Remove this comment to see the full error message */}
                <Icon icon={faCreditCard} type="font-awesome" marginRight="major-1" />
                Card
              </GroupButton>
            )}
            {shouldShowPayInPersonOption && (
              <GroupButton
                // @ts-expect-error TS(2322): Type '{ children: string; onClick: () => void; pa... Remove this comment to see the full error message
                onClick={() => handleSelectPaymentOption(TRANSACTION_PAYMENT_OPTIONS.PAY_IN_PERSON)}
                selected={paymentOption === TRANSACTION_PAYMENT_OPTIONS.PAY_IN_PERSON}
                flex="1"
              >
                In person
              </GroupButton>
            )}
          </Group>
        </Stack>
      )}
      {paymentOption === TRANSACTION_PAYMENT_OPTIONS.PAYMENT_CARD && (
        <PaymentMethod
          isRequired
          onChangePaymentMethod={onChangeCardPaymentMethod}
          // @ts-expect-error TS(2769): No overload matches this call.
          paymentMethods={cardPaymentMethods}
          selectedPaymentMethod={selectedCardPaymentMethod}
          showRememberCardDetails={false}
          onClickRemovePaymentMethod={handleRemovePaymentMethod}
          enableRemovePaymentMethod
        />
      )}
      {paymentOption === TRANSACTION_PAYMENT_OPTIONS.PAY_IN_PERSON && (
        <Box fontSize="300" textAlign="center" role="alert">
          Please pay your provider directly for this service.
        </Box>
      )}
      {showRememberCardDetails && (
        <Set>
          <Field
            name="rememberCardDetails"
            component={CheckboxField.Formik}
            checked={alwaysSavePatientPaymentMethod}
            disabled={alwaysSavePatientPaymentMethod}
            checkboxLabel="Save details for next time"
            marginTop="major-2"
          />
        </Set>
      )}
      {showRememberCardDetails && (
        <Paragraph fontSize="12px" lineHeight="16px">
          By saving card details, you authorise this business to charge your card for future purchases you make with
          them. This authorisation will remain in effect until the card expires. Please contact this business for any
          questions relating to how they may use your saved card details.{' '}
          <Link
            href="https://help.tyrohealth.com/en/articles/6426354-manage-your-saved-card-details"
            target="_blank"
            textDecoration="none"
            rel="noreferrer noopener"
          >
            Learn more
          </Link>
        </Paragraph>
      )}
      <RemovePaymentMethodModal
        modal={removePaymentMethodModal}
        onClickCancel={onClickCancelRemovePaymentMethod}
        onClickSubmit={onClickRemovePaymentMethod}
      />
    </Stack>
  );
}
